// Downloading CDN-linked images into this workspace. Links are fetched through
// /api/fetch-image — a server proxy, because most CDNs send no CORS headers and the browser
// can't read the bytes itself — then uploaded to Supabase storage like any picked file. An
// imported image is therefore indistinguishable from an uploaded one, and survives the
// source link expiring or going private.
//
// Every download is expensive twice over: transfer now, and storage for as long as the
// workspace keeps the product. So the whole path is built to download a given link at most
// once, ever — see importedPathFor for how that's anchored.

import { supabase, apiFetch } from '../lib/supabase';
import { syncImagesFields } from './products';
import { filenameFromUrl, withExtension, isOwnStorageUrl, importedPathFor, downloadUrlFor } from './imageLinks';

const BUCKET = 'product-images';

function publicUrlFor(path) {
  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return publicUrl;
}

/** Fetches one link through the proxy and returns it as a File ready to upload. */
export async function fetchLinkAsFile(url) {
  const res = await apiFetch(`/api/fetch-image?url=${encodeURIComponent(url)}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Couldn’t fetch that image (${res.status}).`);
  }
  const blob = await res.blob();
  const type = blob.type || 'image/jpeg';
  return new File([blob], withExtension(filenameFromUrl(url), type), { type });
}

/**
 * Lists what's already been imported for this tenant, so a link that was fetched on an
 * earlier run is recognised without downloading it again. One listing beats an existence
 * check per image, and a failure here only costs us the optimisation — never the import.
 */
async function listImported(scope) {
  const prefix = `imported/${scope || 'shared'}`;
  const names = new Set();
  try {
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await supabase.storage.from(BUCKET).list(prefix, { limit: 1000, offset });
      if (error || !data?.length) break;
      data.forEach(f => names.add(`${prefix}/${f.name}`));
      if (data.length < 1000) break;
    }
  } catch { /* treat as "nothing known" and fall back to downloading */ }
  return names;
}

/**
 * Re-hosts every image still flagged `remote` (i.e. pointing at someone else's CDN) into
 * this workspace's Supabase bucket, so the link expiring, going private or blocking
 * hotlinks can't break a product later — and so Shopify push sends a URL we control.
 *
 * Downloads are skipped for any link already stored: links that are ours to begin with,
 * links imported on a previous run, and repeats within this run (the same shot listed
 * against several colourways is fetched once and shared).
 *
 * Mutates nothing: returns new product objects. A link that can't be fetched keeps its
 * original URL and stays flagged, so the product still shows *something* and a later
 * import can retry it.
 *
 * Returns { products, imported, reused, errors: [{ url, message }] }.
 */
export async function rehostRemoteImages(products, { scope, concurrency = 4, onProgress } = {}) {
  // Group the work by source link, not by image, so a link used by twenty products is one
  // download that twenty positions then share.
  const byUrl = new Map();
  products.forEach((product, pi) => {
    (product.images || []).forEach((img, ii) => {
      if (!img?.remote || !img.url) return;
      const slots = byUrl.get(img.url) || [];
      slots.push({ pi, ii });
      byUrl.set(img.url, slots);
    });
  });
  if (!byUrl.size) return { products, imported: 0, reused: 0, errors: [] };

  const next = products.map(p => ({ ...p, images: [...(p.images || [])] }));
  const errors = [];
  const sources = [...byUrl.keys()];
  const existing = await listImported(scope);

  let cursor = 0;
  let done = 0;
  let imported = 0;
  let reused = 0;

  const assign = (url, value) => byUrl.get(url).forEach(({ pi, ii }) => {
    next[pi].images[ii] = value;
  });

  const worker = async () => {
    while (cursor < sources.length) {
      const url = sources[cursor++];
      try {
        // Already ours — nothing to fetch, just drop the flag.
        if (isOwnStorageUrl(url)) {
          assign(url, { url, filename: filenameFromUrl(url) });
          reused++;
        } else {
          const path = await importedPathFor(scope, url, filenameFromUrl(url));
          if (existing.has(path)) {
            assign(url, { url: publicUrlFor(path), filename: filenameFromUrl(url), sourceUrl: url });
            reused++;
          } else {
            const file = await fetchLinkAsFile(url);
            // The extension can change once the content type is known, so settle the path
            // against the final filename rather than the one guessed from the URL.
            const finalPath = await importedPathFor(scope, url, file.name);
            const { error } = await supabase.storage.from(BUCKET).upload(finalPath, file, { upsert: true });
            if (error) throw error;
            existing.add(finalPath);
            // sourceUrl records the link this came from. Without it a re-import can't tell
            // the image is already here — the sheet still holds the CDN link, while url now
            // points at our bucket — and only sourceUrl connects the two.
            assign(url, { url: publicUrlFor(finalPath), filename: file.name, sourceUrl: url });
            imported++;
          }
        }
      } catch (e) {
        errors.push({ url, message: e.message });
      }
      onProgress?.(++done, sources.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, sources.length) }, worker));

  // imageUrl / additionalImages were derived from the CDN links at parse time; re-derive
  // them now that the URLs point at our own bucket.
  next.forEach(p => Object.assign(p, syncImagesFields(p.images)));
  return { products: next, imported, reused, errors };
}

/**
 * Saves one image to the person's machine. Images we host get a direct `?download=` link —
 * no bytes through our own infrastructure. An image still on someone else's CDN can't be
 * given a Content-Disposition by us, so it goes through the proxy and is saved from a blob;
 * that path is subject to the proxy's size cap.
 *
 * `saveAs` overrides the name the file lands under, which is how imported imagery gets a
 * meaningful filename instead of whatever the source CDN happened to call it.
 */
export async function downloadProductImage(img, saveAs) {
  const filename = saveAs || img.filename || filenameFromUrl(img.url);
  const direct = downloadUrlFor(img.url, filename);

  let href = direct;
  if (!href) {
    const file = await fetchLinkAsFile(img.url);
    href = URL.createObjectURL(file);
  }

  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Only a blob URL needs releasing, and not before the browser has started the save.
  if (!direct) setTimeout(() => URL.revokeObjectURL(href), 30000);
}

/**
 * Saves every image of a product, one file each, in order. Browsers drop programmatic
 * downloads fired in the same tick, so they're spaced out; Chrome asks once to "allow
 * multiple downloads" and remembers the answer for the site. Keeps going past a failure
 * and returns how many didn't save, so one dead CDN link doesn't cost the rest.
 */
export async function downloadProductImages(images, nameFor, { onProgress } = {}) {
  let failed = 0;
  for (let i = 0; i < images.length; i++) {
    try { await downloadProductImage(images[i], nameFor(images[i], i)); }
    catch { failed++; }
    onProgress?.(i + 1, images.length);
    if (i < images.length - 1) await new Promise(r => setTimeout(r, 400));
  }
  return { failed };
}
